import { DotsLoader } from 'Components/DotsLoader';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { getCompanyShortBySlug } from 'services/company-service';
import { createUser } from 'services/users-service';
import { getTenant } from 'utils/tenant_utils';
import {
  LoginPage,
  LoginCard,
  LogoWrapper,
  LoginHeading,
  FieldGroup,
  FieldLabel,
  FieldError,
  SubmitButton,
  FooterText,
} from './styles';
import { Dialog, TextField } from 'Components/ui';
import { useDispatch } from 'react-redux';
import { updateTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';

export const CreateAccount = () => {
  const { t } = useTranslation(['user']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: company } = useQuery(['getCompanyShortBySlug'], async () => {
    const response = await getCompanyShortBySlug(getTenant());
    return response?.data;
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm({
    defaultValues: { name: '', email: '' },
  });

  const mutation = useMutation(
    newUser => createUser(newUser),
    {
      onError: error => {
        Dialog.alert({
          title: t('user:alert.errorTitle'),
          message: error.response.data.message ?? error.message,
          icon: 'error',
        });
      },
      onSuccess: data => {
        if (data.status === 200 || data.status === 201) {
          dispatch(updateTenant({ tenant: company }));
          navigate(`/verify-code/${data.data.email}`);
        }
      },
    },
  );

  const onSubmit = () => {
    const userData = getValues();
    mutation.mutate({
      name: userData.name,
      email: userData.email,
      active: true,
      permissions: [{ roleKey: 'user_default', companyKey: company.slug }],
    });
  };

  return (
    <LoginPage>
      <LoginCard>
        <LogoWrapper>
          <img src="/img/logo-horizontal.png" height="40" alt="Virtual Officer" style={{ objectFit: 'contain' }} />
        </LogoWrapper>
        <LoginHeading>
          <h1>{t('user:createAccount.heading')}</h1>
          <p>{t('user:createAccount.subtitle')}</p>
        </LoginHeading>
        <FieldGroup>
          <FieldLabel htmlFor="ca-name">{t('user:createAccount.nameLabel')}</FieldLabel>
          <TextField
            id="ca-name"
            type="text"
            fullWidth
            placeholder={t('user:createAccount.namePlaceholder')}
            error={!!errors?.name}
            {...register('name', { required: t('user:users.form.validation.nameRequired') })}
          />
          {errors?.name && <FieldError>{errors.name.message}</FieldError>}
        </FieldGroup>
        <FieldGroup>
          <FieldLabel htmlFor="ca-email">{t('user:login.emailLabel')}</FieldLabel>
          <TextField
            id="ca-email"
            type="email"
            fullWidth
            placeholder={t('user:login.placeholder')}
            error={!!errors?.email}
            {...register('email', { required: t('user:users.form.validation.emailRequired') })}
          />
          {errors?.email && <FieldError>{errors.email.message}</FieldError>}
        </FieldGroup>
        <SubmitButton
          type="submit"
          disabled={mutation.isLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {mutation.isLoading ? <DotsLoader /> : t('user:createAccount.submit')}
        </SubmitButton>
        <FooterText>
          {t('user:createAccount.hasAccount')}{' '}
          <button type="button" onClick={() => navigate('/login')}>
            {t('user:createAccount.loginLink')}
          </button>
        </FooterText>
      </LoginCard>
    </LoginPage>
  );
};
